/**
 * Lightweight i18n.
 *
 * No external dependency. Two locales (en, ru). RU is the default — Storm
 * Games' primary audience is Russian-speaking right now, with English as
 * the second.
 *
 * Persistence: localStorage('storm-locale').
 *
 * Architecture note: Storm Crystals stay as the cross-game premium currency.
 * Battle Passes and soft currencies are per-game (Realms = silver, Survivor =
 * sparks, Daily = none, etc). The portal does NOT advertise a unified BP.
 */

export const LOCALES = ['ru', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

export const LOCALE_LABELS: Record<Locale, string> = {
  ru: 'Русский',
  en: 'English',
};

export const DEFAULT_LOCALE: Locale = 'ru';

// ── Dictionary structure ─────────────────────────────────────────────
export interface Dictionary {
  common: {
    signIn: string;
    signOut: string;
    loading: string;
    retry: string;
    cancel: string;
    confirm: string;
    close: string;
    back: string;
    next: string;
    save: string;
    crystals: string;
    silver: string;
    level: string;
    xp: string;
    score: string;
    time: string;
    kills: string;
    deaths: string;
    rank: string;
    play: string;
    soon: string;
    beta: string;
    live: string;
    new: string;
    victory: string;
    defeat: string;
    draw: string;
  };
  portal: {
    appName: string;
    heroTitle: string;
    heroTaglineLine1: string;
    heroTaglineLine2: string;
    heroCtaSignIn: string;
    heroCtaSignInHint: string;
    chooseStorm: string;
    crossGameNote: string;
    pillar1Title: string;
    pillar1Body: string;
    pillar2Title: string;
    pillar2Body: string;
    pillar3Title: string;
    pillar3Body: string;
    navLobby: string;
    navShop: string;
    navProfile: string;
    games: {
      daily: { name: string; tagline: string };
      survivor: { name: string; tagline: string };
      drop: { name: string; tagline: string };
      tactics: { name: string; tagline: string };
      idle: { name: string; tagline: string };
      arena: { name: string; tagline: string };
      realms: { name: string; tagline: string };
      citylife: { name: string; tagline: string };
      aiwari: { name: string; tagline: string };
    };
  };
  daily: {
    title: string;
    subtitle: string;
    attempt: string;
    cast: string;
    clear: string;
    seed: string;
    howToPlay: string;
    rulesTitle: string;
    rulesIntroLine1: string;
    rulesAttempts: string;
    schoolsTitle: string;
    howToTitle: string;
    rule1: string;
    rule2: string;
    rule3: string;
    feedbackTitle: string;
    exact: string;
    exactHint: string;
    present: string;
    presentHint: string;
    absent: string;
    absentHint: string;
    rewardsTitle: string;
    rewardsBody: string;
    rewardsSignInNote: string;
    rulesCta: string;
    won: string;
    lost: string;
    answerWas: string;
    todayRank: string;
    submittingScore: string;
    xpEarned: string;
    crystalsEarned: string;
    yourScore: string;
    copyShare: string;
    copied: string;
    submitToLeaderboard: string;
    signInToClaim: string;
    tryAnotherGame: string;
    schools: {
      lightning: string;
      tempest: string;
      mist: string;
      frost: string;
      flame: string;
      earth: string;
      light: string;
      shadow: string;
    };
  };
  survivor: {
    title: string;
    pickHero: string;
    heroes: {
      stormcaller: { name: string; bio: string };
      tempestguard: { name: string; bio: string };
      voidblade: { name: string; bio: string };
    };
    beginStorm: string;
    instructions: string;
    instructionsLine2: string;
    levelUpTitle: string;
    pickPower: string;
    upgrades: {
      cooldown: { title: string; desc: string };
      multishot: { title: string; desc: string };
      damage: { title: string; desc: string };
      speed: { title: string; desc: string };
      hp: { title: string; desc: string };
      pickup: { title: string; desc: string };
    };
    abilityQ: string;
    abilityE: string;
    abilityR: string;
    bossWarning: string;
    bossDefeated: string;
    buffShield: string;
    buffSpeed: string;
    buffDamage: string;
    deathTitle: string;
    deathSub: string;
    tryAgain: string;
    backToLobby: string;
    leaderboardTitle: string;
    leaderboardLoading: string;
    leaderboardEmpty: string;
    youAimSettings: string;
    aimAuto: string;
    aimMouse: string;
    autoPick: { title: string; hint: string };
  };
  signIn: {
    welcome: string;
    description: string;
    emailLabel: string;
    emailPlaceholder: string;
    passwordLabel: string;
    passwordPlaceholder: string;
    confirmPasswordLabel: string;
    displayNameLabel: string;
    displayNamePlaceholder: string;
    sendMagicLink: string;
    checkInbox: string;
    checkInboxBody: string;
    useDifferent: string;
    backToLobby: string;
    legalNote: string;
    legalTerms: string;
    legalPrivacy: string;
    tabSignIn: string;
    tabSignUp: string;
    tabMagic: string;
    submitSignIn: string;
    submitSignUp: string;
    googleCta: string;
    or: string;
    forgotPassword: string;
    sendReset: string;
    resetSent: string;
    passwordsMismatch: string;
    passwordTooShort: string;
    needVerification: string;
    needVerificationBody: string;
  };
  profile: {
    cantLoad: string;
    refresh: string;
    honor: string;
    country: string;
    locale: string;
    stormPassActive: string;
    stormPassUntil: string;
    stormPassCta: string;
    stormPassDesc: string;
    subscribe: string;
    shop: string;
  };
  realms: {
    title: string;
    subtitle: string;
    pickClass: string;
    classes: {
      storm: { name: string; bio: string };
      mist: { name: string; bio: string };
      flame: { name: string; bio: string };
      earth: { name: string; bio: string };
      frost: { name: string; bio: string };
      tempest: { name: string; bio: string };
      light: { name: string; bio: string };
      shadow: { name: string; bio: string };
    };
    nameLabel: string;
    namePlaceholder: string;
    create: string;
    yourCharacters: string;
    enterWorld: string;
    deleteCharacter: string;
    confirmDelete: string;
    nextStormIn: string;
    activeStorm: string;
    hp: string;
    mana: string;
    silver: string;
    inventory: string;
    equipment: string;
    skills: string;
    quests: string;
    map: string;
    leaderboard: string;
    zone: string;
    monstersKilled: string;
    bossesKilled: string;
    stormsWitnessed: string;
    levelUp: string;
    bossSpawned: string;
    bossDefeated: string;
    questUpdated: string;
    youDied: string;
    respawn: string;
    saveProgress: string;
    saved: string;
    saving: string;
    /** Class skill names. */
    spells: {
      bolt: { name: string; desc: string };
      gust: { name: string; desc: string };
      barrier: { name: string; desc: string };
      pyre: { name: string; desc: string };
      mistveil: { name: string; desc: string };
      quake: { name: string; desc: string };
    };
    instruction: string;
    controlsTitle: string;
    controlsClick: string;
    controlsKeys: string;
    controlsAbilities: string;
    enterAct: string;
    enterText: string;
    leaveText: string;
    questBanner: string;
    questDone: string;
    monsters: {
      shade: string;
      stalker: string;
      brute: string;
      raider: string;
      warden: string;
    };
    bosses: {
      stormthane: string;
      mistmother: string;
      emberlord: string;
    };
    cycle: string;
    moat: string;
  };
  drop: {
    title: string;
    subtitle: string;
    start: string;
    score: string;
    best: string;
    next: string;
    drop: string;
    gameOver: string;
    tryAgain: string;
    backToLobby: string;
    cascadeBonus: string;
    grandCrystal: string;
    instructionsLine1: string;
    instructionsLine2: string;
    moat: string;
    tiers: { shard: string; bolt: string; strike: string; tempest: string; maelstrom: string; grand: string };
    autoClip: string;
    copyClip: string;
    clipped: string;
  };
  tactics: {
    title: string;
    subtitle: string;
    pickCoven: string;
    schools: { lightning: string; tempest: string; mist: string; frost: string; flame: string; earth: string; light: string; shadow: string };
    queue: string;
    matchStarting: string;
    yourTurn: string;
    enemyTurn: string;
    turn: string;
    energy: string;
    summon: string;
    cast: string;
    pass: string;
    surrender: string;
    matchOverWin: string;
    matchOverLoss: string;
    matchOverDraw: string;
    rematch: string;
    instructionsLine1: string;
    instructionsLine2: string;
    moat: string;
    units: { warrior: string; mage: string; archer: string; healer: string; scout: string };
    yourLeader: string;
    enemyLeader: string;
  };
  idle: {
    title: string;
    subtitle: string;
    schools: { lightning: string; tempest: string; mist: string; frost: string; flame: string; earth: string; light: string; shadow: string };
    spark: string;
    sparkRate: string;
    upgrade: string;
    cost: string;
    owned: string;
    prestige: string;
    prestigeCta: string;
    prestigeHint: string;
    offlineEarnings: string;
    offlineEarningsBody: string;
    autosaved: string;
    moat: string;
    instructionsLine1: string;
    instructionsLine2: string;
    runeMastery: string;
    runeBuyHint: string;
    rune: string;
  };
  arena: {
    title: string;
    subtitle: string;
    pickHero: string;
    roles: {
      assault: string;
      guardian: string;
      marksman: string;
      trickster: string;
    };
    heroes: {
      stormblade: { name: string; bio: string };
      pyromancer: { name: string; bio: string };
      earthwarden: { name: string; bio: string };
      tideguard: { name: string; bio: string };
      voltarcher: { name: string; bio: string };
      hexgunner: { name: string; bio: string };
      mistweaver: { name: string; bio: string };
      runebinder: { name: string; bio: string };
    };
    queueVsBots: string;
    matchStarting: string;
    objectiveLightningRod: string;
    manaNodes: string;
    inversionRune: string;
    stormFront: string;
    stormFrontWarning: string;
    inversionRuneActive: string;
    rodCharging: string;
    rodCaptured: string;
    yourTeam: string;
    enemyTeam: string;
    matchTime: string;
    score: string;
    youKilled: string;
    youDied: string;
    youAssisted: string;
    matchOverWin: string;
    matchOverLoss: string;
    matchOverDraw: string;
    rematch: string;
    submitting: string;
    rewards: string;
    abilityHint: string;
    abilityQ: string;
    abilityE: string;
    abilityR: string;
    moveHint: string;
    aimHint: string;
    moat: string;
  };
}

// ── Russian (default) ────────────────────────────────────────────────
const ru: Dictionary = {
  common: {
    signIn: 'Войти',
    signOut: 'Выйти',
    loading: 'Загрузка…',
    retry: 'Повторить',
    cancel: 'Отмена',
    confirm: 'Подтвердить',
    close: 'Закрыть',
    back: 'Назад',
    next: 'Дальше',
    save: 'Сохранить',
    crystals: 'Кристаллы',
    silver: 'Серебро',
    level: 'Уровень',
    xp: 'Опыт',
    score: 'Очки',
    time: 'Время',
    kills: 'Убийства',
    deaths: 'Смерти',
    rank: 'Ранг',
    play: 'Играть',
    soon: 'Скоро',
    beta: 'Бета',
    live: 'Live',
    new: 'Новое',
    victory: 'Победа',
    defeat: 'Поражение',
    draw: 'Ничья',
  },
  portal: {
    appName: 'Storm Games',
    heroTitle: 'Storm Games',
    heroTaglineLine1: 'Семь игр под одним Storm ID. Каждая со своим миром.',
    heroTaglineLine2: 'Выбирай, во что играть.',
    heroCtaSignIn: 'Войти и играть',
    heroCtaSignInHint: 'Без скачивания. Прямо в браузере.',
    chooseStorm: 'Выбери свой шторм',
    crossGameNote: '7 игр · 1 аккаунт · кристаллы между играми',
    pillar1Title: 'Один аккаунт, семь игр',
    pillar1Body:
      'Storm Crystals — единая премиум-валюта. Косметика, фреймы, аватары, Storm Pass. У каждой игры — свой Battle Pass и своя внутриигровая валюта, потому что жанры разные.',
    pillar2Title: 'Без установок',
    pillar2Body:
      'Открыл — играешь. Через 30 секунд уже в бою. Telegram Mini App и мобильные обёртки — позже. Сейчас просто открой URL.',
    pillar3Title: 'Честная монетизация',
    pillar3Body:
      'Никакого pay-to-win. Никаких рандом-лутбоксов. Никаких фейковых таймеров. Только косметика, Battle Pass и опциональная подписка.',
    navLobby: 'Лобби',
    navShop: 'Магазин',
    navProfile: 'Профиль',
    games: {
      daily: {
        name: 'Storm Cast Daily',
        tagline: 'Расшифруй штормовой сигнал. Раз в день. Глобальный лидерборд.',
      },
      survivor: {
        name: 'Storm Survivor',
        tagline: 'Переживи бурю. Тысячи теней. Одна жизнь.',
      },
      drop: {
        name: 'Storm Drop',
        tagline: 'Кристаллизуй молнию. Каскады славы. Авто-клипы хайлайтов.',
      },
      tactics: {
        name: 'Storm Tactics',
        tagline: 'Собери ковен. Async-дуэли. Пять минут до победы.',
      },
      idle: {
        name: 'Storm Idle',
        tagline: 'Восемь школ. Шторм работает пока ты спишь.',
      },
      arena: {
        name: 'Storm Arena',
        tagline: 'Дуэли магов 3 на 3. Шесть минут. Без установки.',
      },
      realms: {
        name: 'Storm Realms',
        tagline: 'Живой мир, который перепишут штормы каждые 48 часов.',
      },
      citylife: {
        name: 'CityLife',
        tagline: '3D MMO-город, где ты — актёр сериала. Выбирай роль и пиши историю.',
      },
      aiwari: {
        name: 'AIWARI',
        tagline: 'Три ветра, один континент, твоя легенда. Открытый мир MMORPG.',
      },
    },
  },
  daily: {
    title: 'Расшифруй шторм',
    subtitle: 'Угадай ежедневную сигнатуру молнии. {n} попыток.',
    attempt: 'Попытка',
    cast: 'Заклинать',
    clear: 'Очистить',
    seed: 'Сид',
    howToPlay: 'Как играть',
    rulesTitle: 'Как играть — Storm Cast Daily',
    rulesIntroLine1:
      'Каждый день в 00:00 UTC появляется новая сигнатура молнии. Расшифруй её за 6 попыток.',
    rulesAttempts: '6 попыток',
    schoolsTitle: 'Восемь школ магии',
    howToTitle: 'Как угадывать',
    rule1: 'Тапни любую школу под доской — она встанет в следующий пустой слот. Слотов 5.',
    rule2: 'Тапни заполненный слот, чтобы очистить его. Тапни пустой — переместишь курсор.',
    rule3:
      'Когда все 5 слотов заполнены, нажми «Заклинать». Доска покажет какие школы стоят правильно, какие есть в ответе но не в этом слоте, а каких нет совсем.',
    feedbackTitle: 'Чтение подсказок',
    exact: 'Точно',
    exactHint: 'Школа и позиция верны',
    present: 'Есть',
    presentHint: 'Школа в ответе, но не в этом слоте',
    absent: 'Нет',
    absentHint: 'Этой школы нет в ответе',
    rewardsTitle: '⚡ Награды',
    rewardsBody:
      'Реши быстрее — получишь больше. Победа с 1-й попытки — 100 очков, 50 опыта в аккаунт-уровень, 50 Storm Crystals. С каждой попыткой меньше.',
    rewardsSignInNote:
      'Войди, чтобы копить опыт и кристаллы во всех семи играх Storm Games.',
    rulesCta: 'Призвать первую молнию',
    won: 'Расшифровано за {n}!',
    lost: 'Шторм сохранил тайну…',
    answerWas: 'Ответ:',
    todayRank: 'Сегодняшний ранг: №{n}',
    submittingScore: 'Очки записаны. Лидерборд обновляется…',
    xpEarned: 'Опыта получено',
    crystalsEarned: 'Кристаллов получено',
    yourScore: 'Очки',
    copyShare: 'Скопировать сетку',
    copied: 'Скопировано ✓',
    submitToLeaderboard: 'Отправить в лидерборд',
    signInToClaim: 'Войди, чтобы получить опыт и Кристаллы',
    tryAnotherGame: 'Попробовать другую игру →',
    schools: {
      lightning: 'Молния',
      tempest: 'Буря',
      mist: 'Туман',
      frost: 'Мороз',
      flame: 'Пламя',
      earth: 'Земля',
      light: 'Свет',
      shadow: 'Тень',
    },
  },
  survivor: {
    title: 'Storm Survivor',
    pickHero: 'Выбери героя',
    heroes: {
      stormcaller: {
        name: 'Призыватель Шторма',
        bio: 'Молнии бьют далеко и быстро. Сбалансированный.',
      },
      tempestguard: {
        name: 'Страж Бури',
        bio: 'Больше ХП, меньше скорости. Танк со штормовым щитом.',
      },
      voidblade: {
        name: 'Клинок Пустоты',
        bio: 'Хрупкий, но молниеносный. Двойной урон, лёгкая броня.',
      },
    },
    beginStorm: '⚡ Начать шторм',
    instructions:
      'Тени надвигаются из шторма. Ты автоматически бьёшь молниями ближайшую.',
    instructionsLine2:
      'Двигайся: WASD или стрелки на десктопе, виртуальный джойстик слева снизу на телефоне.',
    levelUpTitle: 'Уровень {n}',
    pickPower: 'Выбери силу',
    upgrades: {
      cooldown: { title: 'Быстрый каст', desc: '−15% времени между атаками' },
      multishot: { title: 'Цепная молния', desc: '+1 молния за каст' },
      damage: { title: 'Сила шторма', desc: '+1 урона молнии' },
      speed: { title: 'Поступь ветра', desc: '+15% скорости движения' },
      hp: { title: 'Стойкая душа', desc: '+1 макс. ХП и полное лечение' },
      pickup: { title: 'Магнетизм', desc: '+50% радиуса сбора опыта' },
    },
    abilityQ: 'Q — Удар шторма',
    abilityE: 'E — Рывок ветра',
    abilityR: 'R — Щит шторма',
    bossWarning: '⚠️ Приближается босс!',
    bossDefeated: 'Босс повержен!',
    buffShield: 'Неуязвимость 6 с',
    buffSpeed: 'Скорость +50% на 12 с',
    buffDamage: 'Урон ×2 на 10 с',
    deathTitle: 'Шторм поглотил тебя',
    deathSub: '',
    tryAgain: '⚡ Снова',
    backToLobby: '← В лобби',
    leaderboardTitle: 'Топ за сегодня',
    leaderboardLoading: 'Загрузка лидерборда…',
    leaderboardEmpty: 'Будь первым в сегодняшнем топе.',
    youAimSettings: 'Прицеливание',
    aimAuto: 'Авто (ближайший)',
    aimMouse: 'Мышью',
    autoPick: {
      title: 'Авто-выбор апгрейдов по приоритету',
      hint: 'При повышении уровня игра сама подберёт апгрейд из предложенных по твоим приоритетам. Перетаскивай стрелками — сверху самое желанное.',
    },
  },
  signIn: {
    welcome: 'Добро пожаловать в Storm Games',
    description: 'Войди с паролем, через Google или magic-ссылкой на email.',
    emailLabel: 'Email',
    emailPlaceholder: 'you@example.com',
    passwordLabel: 'Пароль',
    passwordPlaceholder: 'минимум 8 символов',
    confirmPasswordLabel: 'Повтори пароль',
    displayNameLabel: 'Никнейм',
    displayNamePlaceholder: 'Грозовой',
    sendMagicLink: 'Отправить magic-ссылку',
    checkInbox: 'Проверь почту',
    checkInboxBody: 'Мы отправили ссылку для входа на {email}. Действует 1 час.',
    useDifferent: 'Ввести другой email',
    backToLobby: '← Назад в лобби',
    legalNote: 'Продолжая, ты соглашаешься с',
    legalTerms: 'условиями',
    legalPrivacy: 'и политикой конфиденциальности',
    tabSignIn: 'Войти',
    tabSignUp: 'Регистрация',
    tabMagic: 'Magic link',
    submitSignIn: 'Войти',
    submitSignUp: 'Создать аккаунт',
    googleCta: 'Войти через Google',
    or: 'или',
    forgotPassword: 'Забыл пароль?',
    sendReset: 'Сбросить пароль',
    resetSent: 'Письмо для сброса отправлено на {email}',
    passwordsMismatch: 'Пароли не совпадают',
    passwordTooShort: 'Пароль должен быть не короче 8 символов',
    needVerification: 'Подтверди почту',
    needVerificationBody: 'Мы отправили письмо для активации на {email}. Кликни ссылку — войдёшь автоматически.',
  },
  profile: {
    cantLoad: 'Не получилось загрузить профиль.',
    refresh: 'Обновить страницу',
    honor: 'Репутация',
    country: 'Страна',
    locale: 'Язык',
    stormPassActive: 'Storm Pass активен',
    stormPassUntil: 'до {date}',
    stormPassCta: 'Storm Pass',
    stormPassDesc: '+20% опыта · ежедневные кристаллы · без рекламы',
    subscribe: 'Оформить →',
    shop: 'Магазин',
  },
  realms: {
    title: 'Storm Realms',
    subtitle: 'Живой мир. Восемь школ магии. Шторм переписывает зоны каждые 48 часов.',
    pickClass: 'Выбери школу магии',
    classes: {
      storm: {
        name: 'Призыватель Молнии',
        bio: 'Дальние молнии. Цепные удары. Хрупкий, но смертельный.',
      },
      mist: {
        name: 'Странник Тумана',
        bio: 'Невидимость. Подкрадывается из тени. Кинжал-эхо.',
      },
      flame: {
        name: 'Кузнец Пламени',
        bio: 'Горящие зоны. Урон во времени. Стойкий маг ближнего боя.',
      },
      earth: {
        name: 'Хранитель Земли',
        bio: 'Каменные щиты. Землетрясения. Танк с тяжёлым уроном.',
      },
      frost: {
        name: 'Голос Мороза',
        bio: 'Замораживает врагов. Замедление. Ледяные стены.',
      },
      tempest: {
        name: 'Глаз Бури',
        bio: 'Вихри. Отбрасывание. Контроль толпы.',
      },
      light: {
        name: 'Светоносец',
        bio: 'Хиллер. Барьеры. Священные удары по тёмным.',
      },
      shadow: {
        name: 'Тенепряд',
        bio: 'Высасывает жизнь. Призывает копии. Тёмная магия.',
      },
    },
    nameLabel: 'Имя персонажа',
    namePlaceholder: 'Гром, Мглой, …',
    create: 'Создать персонажа',
    yourCharacters: 'Твои персонажи',
    enterWorld: 'Войти в мир',
    deleteCharacter: 'Удалить',
    confirmDelete: 'Удалить персонажа? Прогресс будет потерян.',
    nextStormIn: 'Следующий шторм через {time}',
    activeStorm: 'Активный шторм: {school}',
    hp: 'ХП',
    mana: 'Мана',
    silver: 'Серебро',
    inventory: 'Инвентарь',
    equipment: 'Снаряжение',
    skills: 'Умения',
    quests: 'Задания',
    map: 'Карта',
    leaderboard: 'Лидерборд',
    zone: 'Зона',
    monstersKilled: 'Чудовищ убито',
    bossesKilled: 'Боссов повержено',
    stormsWitnessed: 'Штормов пережито',
    levelUp: 'Уровень повышен!',
    bossSpawned: '⚠️ Появился босс — {name}',
    bossDefeated: 'Босс повержен!',
    questUpdated: 'Задание обновлено',
    youDied: 'Ты пал в бою',
    respawn: 'Возродиться',
    saveProgress: 'Сохранить прогресс',
    saved: 'Сохранено ✓',
    saving: 'Сохранение…',
    spells: {
      bolt: { name: 'Молния', desc: 'Быстрый дальний удар током.' },
      gust: { name: 'Порыв', desc: 'Отталкивает врагов вокруг.' },
      barrier: { name: 'Барьер', desc: 'Поглощает урон 6 с.' },
      pyre: { name: 'Костёр', desc: 'Зона горения. Урон во времени.' },
      mistveil: { name: 'Туманная вуаль', desc: 'Невидимость 4 с, +50% к урону по выходу.' },
      quake: { name: 'Землетрясение', desc: 'Замедляет всех вокруг и оглушает.' },
    },
    instruction: 'Кликом мыши — двигаешься или атакуешь. Клавишами 1/2/3 — заклинания.',
    controlsTitle: 'Управление',
    controlsClick: 'Клик ЛКМ — двигаться или атаковать цель',
    controlsKeys: 'WASD / стрелки — движение клавишами',
    controlsAbilities: '1, 2, 3 — заклинания школы',
    enterAct: 'Войти',
    enterText: 'Войти в зону',
    leaveText: 'Назад',
    questBanner: 'Текущее задание',
    questDone: 'Задание выполнено',
    monsters: {
      shade: 'Тень',
      stalker: 'Стелс-преследователь',
      brute: 'Громила',
      raider: 'Разбойник',
      warden: 'Страж',
    },
    bosses: {
      stormthane: 'Тэн Шторма',
      mistmother: 'Мать-в-Тумане',
      emberlord: 'Владыка Углей',
    },
    cycle: 'Цикл',
    moat: 'Каждые 48 часов мир переписывают штормы.',
  },
  drop: {
    title: 'Storm Drop',
    subtitle: 'Кристаллизуй молнию. Каскады славы. Автоклип хайлайта.',
    start: 'Начать',
    score: 'Очки',
    best: 'Лучший',
    next: 'Следующее',
    drop: 'Бросить',
    gameOver: 'Котёл переполнен',
    tryAgain: 'Снова',
    backToLobby: '← В лобби',
    cascadeBonus: 'Каскад ×{n}!',
    grandCrystal: '⚡ Великий Кристалл!',
    instructionsLine1: 'Бросай молнии в котёл — две одинаковые сливаются в большую. От искры до Великого Кристалла — 6 ступеней.',
    instructionsLine2: 'Касание / клик — двигай молнию. Тап «Бросить» — отпускай. Не дай котлу переполниться.',
    moat: 'Твой каскадный реплей. Поделись клипом и обгони друзей в лидерборде.',
    tiers: {
      shard: 'Искра',
      bolt: 'Молния',
      strike: 'Удар',
      tempest: 'Буря',
      maelstrom: 'Ураган',
      grand: 'Великий Кристалл',
    },
    autoClip: 'Авто-клип',
    copyClip: 'Скопировать клип',
    clipped: 'Скопировано ✓',
  },
  tactics: {
    title: 'Storm Tactics',
    subtitle: 'Собери ковен. Ход за ходом. Пять минут до победы.',
    pickCoven: 'Собери ковен',
    schools: {
      lightning: 'Молния',
      tempest: 'Буря',
      mist: 'Туман',
      frost: 'Мороз',
      flame: 'Пламя',
      earth: 'Земля',
      light: 'Свет',
      shadow: 'Тень',
    },
    queue: 'Бой с ИИ',
    matchStarting: 'Матч начинается…',
    yourTurn: 'Твой ход',
    enemyTurn: 'Ход противника',
    turn: 'Ход',
    energy: 'Энергия',
    summon: 'Призвать',
    cast: 'Заклинать',
    pass: 'Пропустить ход',
    surrender: 'Сдаться',
    matchOverWin: 'Победа',
    matchOverLoss: 'Поражение',
    matchOverDraw: 'Ничья',
    rematch: 'Реванш',
    instructionsLine1: 'Покупай юнитов за энергию и расставляй их на гексах. Атакуй вражеского лидера.',
    instructionsLine2: 'Каждый юнит из своей школы — он усилен на тайле своей стихии. Используй гексы умно.',
    moat: 'Восемь школ магии. Async-дуэли. Победа за 5 минут.',
    units: {
      warrior: 'Воин',
      mage: 'Маг',
      archer: 'Лучник',
      healer: 'Целитель',
      scout: 'Разведчик',
    },
    yourLeader: 'Твой лидер',
    enemyLeader: 'Лидер врага',
  },
  idle: {
    title: 'Storm Idle',
    subtitle: 'Восемь школ. Бесконечный рост. Шторм работает пока ты спишь.',
    schools: {
      lightning: 'Молния',
      tempest: 'Буря',
      mist: 'Туман',
      frost: 'Мороз',
      flame: 'Пламя',
      earth: 'Земля',
      light: 'Свет',
      shadow: 'Тень',
    },
    spark: 'Искры',
    sparkRate: 'Искр в секунду',
    upgrade: 'Прокачать',
    cost: 'Цена',
    owned: 'Куплено',
    prestige: 'Престиж',
    prestigeCta: 'Сбросить с престижем',
    prestigeHint: 'Сбрось искры и получи Руны Шторма — постоянный множитель.',
    offlineEarnings: 'Доход за время отсутствия',
    offlineEarningsBody: 'Ты накопил {amount} искр пока тебя не было.',
    autosaved: 'Автосохранение ✓',
    moat: 'Каждые 60 секунд активный шторм усиливает одну школу на ×3.',
    instructionsLine1: 'Кликай на кристалл — получай искры. Покупай школы магии — они приносят искры пассивно.',
    instructionsLine2: 'Накопил много? Сбрось с престижем — получишь Руны Шторма, которые ускоряют всё после сброса.',
    runeMastery: 'Мастерство Рун',
    runeBuyHint: '+5% ко всему за каждую Руну',
    rune: 'Руна',
  },
  arena: {
    title: 'Storm Arena',
    subtitle: 'Шесть минут. Три на три. Одна арена. Без пощады.',
    pickHero: 'Выбери героя',
    roles: {
      assault: 'Штурмовик',
      guardian: 'Страж',
      marksman: 'Стрелок',
      trickster: 'Хитрец',
    },
    heroes: {
      stormblade: { name: 'Громоклинок', bio: 'Ближний бой. Удар-разрядка.' },
      pyromancer: { name: 'Пиромант', bio: 'Огонь по площади. Прыгает в гущу.' },
      earthwarden: { name: 'Землестраж', bio: 'Каменная стена. Защищает союзников.' },
      tideguard: { name: 'Стражприлива', bio: 'Затягивает врагов. Снимает бафы.' },
      voltarcher: { name: 'Вольтлучник', bio: 'Цепная молния-самонаводка.' },
      hexgunner: { name: 'Хексган', bio: 'Рикошет от стен. Скилшоты.' },
      mistweaver: { name: 'Тканытумана', bio: 'Невидимость. Ставит ловушки.' },
      runebinder: { name: 'Рунный', bio: 'Ворует ману с узлов противника.' },
    },
    queueVsBots: 'Бой с ботами 3v3',
    matchStarting: 'Матч начинается…',
    objectiveLightningRod: 'Громоотвод заряжается через {time} с',
    manaNodes: 'Узлы маны',
    inversionRune: 'Руна Инверсии',
    stormFront: 'Штормовой фронт',
    stormFrontWarning: '⚠️ Через 5 секунд по карте пройдёт штормовой фронт',
    inversionRuneActive: 'Руна Инверсии активна — роль изменена на {role}',
    rodCharging: 'Громоотвод заряжается: {pct}%',
    rodCaptured: 'Громоотвод захвачен!',
    yourTeam: 'Твоя команда',
    enemyTeam: 'Враги',
    matchTime: 'Время',
    score: 'Счёт',
    youKilled: 'Ты убил {name}',
    youDied: 'Ты убит героем {name}',
    youAssisted: 'Ассист',
    matchOverWin: 'Победа',
    matchOverLoss: 'Поражение',
    matchOverDraw: 'Ничья',
    rematch: 'Реванш',
    submitting: 'Запись результата…',
    rewards: 'Награда: {xp} опыта · {crystals} 💎',
    abilityHint: 'Q / E / R — способности',
    abilityQ: 'Q',
    abilityE: 'E',
    abilityR: 'R (ульт)',
    moveHint: 'WASD — движение',
    aimHint: 'Мышь — прицел',
    moat: 'Storm Front. Узлы маны. Руна Инверсии. Три механики, которых нет нигде.',
  },
};

// ── English ──────────────────────────────────────────────────────────
const en: Dictionary = {
  common: {
    signIn: 'Sign in',
    signOut: 'Sign out',
    loading: 'Loading…',
    retry: 'Retry',
    cancel: 'Cancel',
    confirm: 'Confirm',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    save: 'Save',
    crystals: 'Crystals',
    silver: 'Silver',
    level: 'Level',
    xp: 'XP',
    score: 'Score',
    time: 'Time',
    kills: 'Kills',
    deaths: 'Deaths',
    rank: 'Rank',
    play: 'Play',
    soon: 'Soon',
    beta: 'Beta',
    live: 'Live',
    new: 'New',
    victory: 'Victory',
    defeat: 'Defeat',
    draw: 'Draw',
  },
  portal: {
    appName: 'Storm Games',
    heroTitle: 'Storm Games',
    heroTaglineLine1: 'Seven games under one Storm ID. Each its own world.',
    heroTaglineLine2: 'Pick what you want to play.',
    heroCtaSignIn: 'Sign in to play',
    heroCtaSignInHint: 'No download. Browser only.',
    chooseStorm: 'Choose your storm',
    crossGameNote: '7 games · 1 account · crystals across games',
    pillar1Title: 'One account, seven games',
    pillar1Body:
      'Storm Crystals are the cross-game premium currency — cosmetics, frames, avatars, Storm Pass. Each game has its own Battle Pass and its own soft currency, because the genres differ.',
    pillar2Title: 'No installs',
    pillar2Body:
      'Open and play. In combat in 30 seconds. Telegram Mini App and mobile-store wrappers later — for now, just open the URL.',
    pillar3Title: 'Honest monetisation',
    pillar3Body:
      'No pay-to-win. No random loot boxes. No fake countdown timers. Cosmetics, Battle Pass, optional subscription. That is it.',
    navLobby: 'Lobby',
    navShop: 'Shop',
    navProfile: 'Profile',
    games: {
      daily: { name: 'Storm Cast Daily', tagline: 'Decode the storm. Once a day. Worldwide.' },
      survivor: { name: 'Storm Survivor', tagline: 'Outlast the tempest. Ten thousand bolts. One life.' },
      drop: { name: 'Storm Drop', tagline: 'Crystallise lightning. Cascade glory.' },
      tactics: { name: 'Storm Tactics', tagline: 'Draft a coven. Async duels. Five minutes to victory.' },
      idle: { name: 'Storm Idle', tagline: 'Eight schools. The storm works while you sleep.' },
      arena: { name: 'Storm Arena', tagline: '3v3 mage duels. Six minutes. No install.' },
      realms: { name: 'Storm Realms', tagline: 'A persistent world rewritten by storms every 48 hours.' },
      citylife: { name: 'CityLife', tagline: '3D MMO city where you star in the series. Pick a role, shape the story.' },
      aiwari: { name: 'AIWARI', tagline: 'Three Winds. One continent. Your legend. Open-world MMORPG.' },
    },
  },
  daily: {
    title: 'Decode the storm',
    subtitle: "Guess the day's lightning signature. {n} attempts.",
    attempt: 'Attempt',
    cast: 'Cast',
    clear: 'Clear',
    seed: 'Seed',
    howToPlay: 'How to play',
    rulesTitle: 'How to play — Storm Cast Daily',
    rulesIntroLine1:
      'Every day at 00:00 UTC a new lightning signature appears. Decode it in 6 attempts.',
    rulesAttempts: '6 attempts',
    schoolsTitle: 'The eight schools of magic',
    howToTitle: 'How to guess',
    rule1: 'Tap any school below the board to fill the next empty slot. There are 5 slots.',
    rule2: 'Tap a filled slot to clear it. Tap an empty slot to move the cursor.',
    rule3:
      'When all 5 slots are filled, hit Cast. The board shows which schools were exact, present elsewhere, or absent.',
    feedbackTitle: 'Reading the feedback',
    exact: 'Exact',
    exactHint: 'School and position both correct',
    present: 'Present',
    presentHint: 'School is in the answer, but not here',
    absent: 'Absent',
    absentHint: 'Not in the answer',
    rewardsTitle: '⚡ Rewards',
    rewardsBody:
      'Solve faster — earn more. Win in 1 attempt = 100 score, 50 XP, 50 Storm Crystals. Score scales down with each attempt.',
    rewardsSignInNote:
      'Sign in to bank XP & crystals across all seven games of Storm Games.',
    rulesCta: 'Cast my first spell',
    won: 'Decoded in {n}!',
    lost: 'Storm holds its secrets…',
    answerWas: 'Answer:',
    todayRank: "Today's rank: #{n}",
    submittingScore: 'Score logged. Worldwide leaderboard updating…',
    xpEarned: 'XP earned',
    crystalsEarned: 'Crystals earned',
    yourScore: 'Score',
    copyShare: 'Copy share-grid',
    copied: 'Copied ✓',
    submitToLeaderboard: 'Submit to leaderboard',
    signInToClaim: 'Sign in to claim XP & Crystals',
    tryAnotherGame: 'Try another game →',
    schools: {
      lightning: 'Lightning',
      tempest: 'Tempest',
      mist: 'Mist',
      frost: 'Frost',
      flame: 'Flame',
      earth: 'Earth',
      light: 'Light',
      shadow: 'Shadow',
    },
  },
  survivor: {
    title: 'Storm Survivor',
    pickHero: 'Pick a hero',
    heroes: {
      stormcaller: { name: 'Storm Caller', bio: 'Lightning hits far and fast. Balanced kit.' },
      tempestguard: { name: 'Tempest Guard', bio: 'More HP, less speed. Tank with a storm aegis.' },
      voidblade: { name: 'Void Blade', bio: 'Fragile but lightning-fast. Double damage, light armour.' },
    },
    beginStorm: '⚡ Begin the storm',
    instructions: 'Shadows pour from the storm. You auto-cast lightning at the nearest one.',
    instructionsLine2: 'Move with WASD / arrows on desktop, virtual joystick bottom-left on phone.',
    levelUpTitle: 'Level {n}',
    pickPower: 'Choose a power',
    upgrades: {
      cooldown: { title: 'Faster Casting', desc: '−15% attack cooldown' },
      multishot: { title: 'Chain Lightning', desc: '+1 bolt per cast' },
      damage: { title: 'Storm Strength', desc: '+1 bolt damage' },
      speed: { title: 'Wind Stride', desc: '+15% movement speed' },
      hp: { title: 'Resilient Soul', desc: '+1 max HP & full heal' },
      pickup: { title: 'Magnetism', desc: '+50% XP pickup range' },
    },
    abilityQ: 'Q — Storm Strike',
    abilityE: 'E — Wind Dash',
    abilityR: 'R — Storm Shield',
    bossWarning: '⚠️ Boss incoming!',
    bossDefeated: 'Boss defeated!',
    buffShield: 'Invulnerable 6s',
    buffSpeed: 'Speed +50% 12s',
    buffDamage: 'Damage ×2 for 10s',
    deathTitle: 'The storm consumed you',
    deathSub: '',
    tryAgain: '⚡ Try again',
    backToLobby: '← Lobby',
    leaderboardTitle: "Today's top",
    leaderboardLoading: 'Loading leaderboard…',
    leaderboardEmpty: 'Be the first on the board today.',
    youAimSettings: 'Aim',
    aimAuto: 'Auto (closest)',
    aimMouse: 'Mouse',
    autoPick: {
      title: 'Auto-pick upgrades by priority',
      hint: 'On level-up, the game auto-picks the highest-priority offered upgrade. Reorder below — top = most wanted.',
    },
  },
  signIn: {
    welcome: 'Welcome to Storm Games',
    description: 'Sign in with password, Google, or a magic email link.',
    emailLabel: 'Email',
    emailPlaceholder: 'you@example.com',
    passwordLabel: 'Password',
    passwordPlaceholder: 'min 8 characters',
    confirmPasswordLabel: 'Repeat password',
    displayNameLabel: 'Display name',
    displayNamePlaceholder: 'Stormcaller',
    sendMagicLink: 'Send magic link',
    checkInbox: 'Check your inbox',
    checkInboxBody: 'We sent a sign-in link to {email}. Works for 1 hour.',
    useDifferent: 'Use a different email',
    backToLobby: '← Back to lobby',
    legalNote: 'By continuing you agree to the',
    legalTerms: 'Terms',
    legalPrivacy: 'and Privacy Policy',
    tabSignIn: 'Sign in',
    tabSignUp: 'Register',
    tabMagic: 'Magic link',
    submitSignIn: 'Sign in',
    submitSignUp: 'Create account',
    googleCta: 'Continue with Google',
    or: 'or',
    forgotPassword: 'Forgot password?',
    sendReset: 'Reset password',
    resetSent: 'Reset email sent to {email}',
    passwordsMismatch: 'Passwords do not match',
    passwordTooShort: 'Password must be at least 8 characters',
    needVerification: 'Confirm your email',
    needVerificationBody: 'We sent an activation link to {email}. Click it — you will be signed in automatically.',
  },
  profile: {
    cantLoad: 'Could not load profile.',
    refresh: 'Refresh page',
    honor: 'Honor',
    country: 'Country',
    locale: 'Locale',
    stormPassActive: 'Storm Pass active',
    stormPassUntil: 'until {date}',
    stormPassCta: 'Storm Pass',
    stormPassDesc: '+20% XP · daily crystals · ad-free',
    subscribe: 'Subscribe →',
    shop: 'Shop',
  },
  realms: {
    title: 'Storm Realms',
    subtitle: 'A living world. Eight schools of magic. Storms rewrite zones every 48 hours.',
    pickClass: 'Choose your school',
    classes: {
      storm: { name: 'Storm Caller', bio: 'Long-range bolts. Chain hits. Fragile but lethal.' },
      mist: { name: 'Mist Walker', bio: 'Stealth. Backstab from shadow. Echo-blade.' },
      flame: { name: 'Flame Forged', bio: 'Burning ground. Damage over time. Sturdy melee mage.' },
      earth: { name: 'Earth Warden', bio: 'Stone shields. Earthquakes. Tank with heavy hits.' },
      frost: { name: 'Frost Voice', bio: 'Freezes enemies. Slows. Ice walls.' },
      tempest: { name: 'Eye of Tempest', bio: 'Vortices. Knockback. Crowd control.' },
      light: { name: 'Lightbearer', bio: 'Healer. Barriers. Holy strikes against shadow.' },
      shadow: { name: 'Shadeweaver', bio: 'Life drain. Summons copies. Dark magic.' },
    },
    nameLabel: 'Character name',
    namePlaceholder: 'Thora, Mistveil, …',
    create: 'Create character',
    yourCharacters: 'Your characters',
    enterWorld: 'Enter the world',
    deleteCharacter: 'Delete',
    confirmDelete: 'Delete this character? Progress will be lost.',
    nextStormIn: 'Next storm in {time}',
    activeStorm: 'Active storm: {school}',
    hp: 'HP',
    mana: 'Mana',
    silver: 'Silver',
    inventory: 'Inventory',
    equipment: 'Equipment',
    skills: 'Skills',
    quests: 'Quests',
    map: 'Map',
    leaderboard: 'Leaderboard',
    zone: 'Zone',
    monstersKilled: 'Monsters killed',
    bossesKilled: 'Bosses defeated',
    stormsWitnessed: 'Storms witnessed',
    levelUp: 'Level up!',
    bossSpawned: '⚠️ A boss appears — {name}',
    bossDefeated: 'Boss defeated!',
    questUpdated: 'Quest updated',
    youDied: 'You fell in battle',
    respawn: 'Respawn',
    saveProgress: 'Save progress',
    saved: 'Saved ✓',
    saving: 'Saving…',
    spells: {
      bolt: { name: 'Bolt', desc: 'Fast ranged shock.' },
      gust: { name: 'Gust', desc: 'Knocks back enemies around you.' },
      barrier: { name: 'Barrier', desc: 'Absorbs damage for 6s.' },
      pyre: { name: 'Pyre', desc: 'Burning ground. Damage over time.' },
      mistveil: { name: 'Mist Veil', desc: 'Invisible 4s, +50% damage on exit.' },
      quake: { name: 'Quake', desc: 'Slows everyone around and stuns.' },
    },
    instruction: 'Click to move or attack. Keys 1/2/3 — spells of your school.',
    controlsTitle: 'Controls',
    controlsClick: 'LMB click — move or attack the target',
    controlsKeys: 'WASD / arrows — keyboard movement',
    controlsAbilities: '1, 2, 3 — school spells',
    enterAct: 'Enter',
    enterText: 'Enter zone',
    leaveText: 'Back',
    questBanner: 'Current quest',
    questDone: 'Quest complete',
    monsters: {
      shade: 'Shade',
      stalker: 'Stalker',
      brute: 'Brute',
      raider: 'Raider',
      warden: 'Warden',
    },
    bosses: {
      stormthane: 'Storm Thane',
      mistmother: 'Mist Mother',
      emberlord: 'Ember Lord',
    },
    cycle: 'Cycle',
    moat: 'Every 48 hours, storms rewrite the world.',
  },
  drop: {
    title: 'Storm Drop',
    subtitle: 'Crystallise lightning. Cascade glory. Auto-clip the highlight.',
    start: 'Start',
    score: 'Score',
    best: 'Best',
    next: 'Next',
    drop: 'Drop',
    gameOver: 'Cauldron overflows',
    tryAgain: 'Try again',
    backToLobby: '← Lobby',
    cascadeBonus: 'Cascade ×{n}!',
    grandCrystal: '⚡ Grand Crystal!',
    instructionsLine1: 'Drop bolts into the cauldron — two of a kind merge into the next tier. From spark to Grand Crystal — six tiers.',
    instructionsLine2: 'Touch / click — aim. Tap Drop — release. Don\'t let the cauldron overflow.',
    moat: 'Your cascade replay. Share the clip and climb the leaderboard.',
    tiers: {
      shard: 'Spark',
      bolt: 'Bolt',
      strike: 'Strike',
      tempest: 'Tempest',
      maelstrom: 'Maelstrom',
      grand: 'Grand Crystal',
    },
    autoClip: 'Auto-clip',
    copyClip: 'Copy clip',
    clipped: 'Copied ✓',
  },
  tactics: {
    title: 'Storm Tactics',
    subtitle: 'Draft a coven. Turn by turn. Five minutes to victory.',
    pickCoven: 'Pick your coven',
    schools: {
      lightning: 'Lightning',
      tempest: 'Tempest',
      mist: 'Mist',
      frost: 'Frost',
      flame: 'Flame',
      earth: 'Earth',
      light: 'Light',
      shadow: 'Shadow',
    },
    queue: 'Match vs AI',
    matchStarting: 'Match starting…',
    yourTurn: 'Your turn',
    enemyTurn: 'Enemy turn',
    turn: 'Turn',
    energy: 'Energy',
    summon: 'Summon',
    cast: 'Cast',
    pass: 'Pass',
    surrender: 'Surrender',
    matchOverWin: 'Victory',
    matchOverLoss: 'Defeat',
    matchOverDraw: 'Draw',
    rematch: 'Rematch',
    instructionsLine1: 'Buy units with energy and place them on hexes. Attack the enemy leader.',
    instructionsLine2: 'Units stand on their school\'s tile to gain a buff. Use hexes wisely.',
    moat: 'Eight magic schools. Async duels. Five minutes to win.',
    units: {
      warrior: 'Warrior',
      mage: 'Mage',
      archer: 'Archer',
      healer: 'Healer',
      scout: 'Scout',
    },
    yourLeader: 'Your leader',
    enemyLeader: 'Enemy leader',
  },
  idle: {
    title: 'Storm Idle',
    subtitle: 'Eight schools. Endless growth. The storm works while you sleep.',
    schools: {
      lightning: 'Lightning',
      tempest: 'Tempest',
      mist: 'Mist',
      frost: 'Frost',
      flame: 'Flame',
      earth: 'Earth',
      light: 'Light',
      shadow: 'Shadow',
    },
    spark: 'Sparks',
    sparkRate: 'Sparks per second',
    upgrade: 'Upgrade',
    cost: 'Cost',
    owned: 'Owned',
    prestige: 'Prestige',
    prestigeCta: 'Reset with prestige',
    prestigeHint: 'Reset your sparks and earn Storm Runes — a permanent multiplier.',
    offlineEarnings: 'Offline earnings',
    offlineEarningsBody: 'You banked {amount} sparks while away.',
    autosaved: 'Saved ✓',
    moat: 'Every 60 seconds, the active storm boosts one school by ×3.',
    instructionsLine1: 'Click the crystal — gain sparks. Buy schools of magic — they earn sparks passively.',
    instructionsLine2: 'Stockpiled? Prestige reset — earn Storm Runes that accelerate everything next run.',
    runeMastery: 'Rune Mastery',
    runeBuyHint: '+5% to everything per rune',
    rune: 'Rune',
  },
  arena: {
    title: 'Storm Arena',
    subtitle: 'Six minutes. Three on three. One arena. No mercy.',
    pickHero: 'Pick a hero',
    roles: {
      assault: 'Assault',
      guardian: 'Guardian',
      marksman: 'Marksman',
      trickster: 'Trickster',
    },
    heroes: {
      stormblade: { name: 'Stormblade', bio: 'Melee. Discharge strike.' },
      pyromancer: { name: 'Pyromancer', bio: 'Area fire. Dives the pile.' },
      earthwarden: { name: 'Earth Warden', bio: 'Stone wall. Peels for allies.' },
      tideguard: { name: 'Tide Guard', bio: 'Pulls enemies. Strips buffs.' },
      voltarcher: { name: 'Voltarcher', bio: 'Chain-lightning autos.' },
      hexgunner: { name: 'Hex Gunner', bio: 'Wall ricochet skillshots.' },
      mistweaver: { name: 'Mist Weaver', bio: 'Stealth. Trap-setter.' },
      runebinder: { name: 'Rune Binder', bio: 'Steals mana from enemy nodes.' },
    },
    queueVsBots: 'Quick match vs bots 3v3',
    matchStarting: 'Match starting…',
    objectiveLightningRod: 'Lightning Rod charges in {time}s',
    manaNodes: 'Mana Nodes',
    inversionRune: 'Inversion Rune',
    stormFront: 'Storm Front',
    stormFrontWarning: '⚠️ Storm Front sweeps the map in 5 seconds',
    inversionRuneActive: 'Inversion Rune active — role swapped to {role}',
    rodCharging: 'Lightning Rod charging: {pct}%',
    rodCaptured: 'Lightning Rod captured!',
    yourTeam: 'Your team',
    enemyTeam: 'Enemy',
    matchTime: 'Time',
    score: 'Score',
    youKilled: 'You killed {name}',
    youDied: 'You were killed by {name}',
    youAssisted: 'Assist',
    matchOverWin: 'Victory',
    matchOverLoss: 'Defeat',
    matchOverDraw: 'Draw',
    rematch: 'Rematch',
    submitting: 'Submitting result…',
    rewards: 'Reward: {xp} XP · {crystals} 💎',
    abilityHint: 'Q / E / R — abilities',
    abilityQ: 'Q',
    abilityE: 'E',
    abilityR: 'R (ult)',
    moveHint: 'WASD — move',
    aimHint: 'Mouse — aim',
    moat: 'Storm Front. Mana Nodes. Inversion Rune. Three mechanics that exist nowhere else.',
  },
};

const dictionaries: Record<Locale, Dictionary> = { ru, en };

export function getDictionary(locale: Locale): Dictionary {
  return dictionaries[locale] ?? dictionaries[DEFAULT_LOCALE];
}

export function readLocale(): Locale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  const v = window.localStorage.getItem('storm-locale');
  if (v && (LOCALES as readonly string[]).includes(v)) return v as Locale;
  return DEFAULT_LOCALE;
}

export function writeLocale(locale: Locale): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem('storm-locale', locale);
}

export function template(s: string, vars: Record<string, string | number>): string {
  return s.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`));
}
